/**
 * Hub PDP booking: city card → this city's sessions (date window) → store calendar or popup.
 * One city = one location. Uses workshop-city/events, not Center events-v.
 *
 * First paint loads today → end of current month + lookahead (not the full catalog).
 * Store calendar month changes request the next window. If the engine still returns
 * a multi-year dump, matching slots are indexed by month so later months do not refetch.
 */
(function (global) {
  'use strict';
  if (global.WorkshopBookingWizardHub) return;

  var STORE_LOOKAHEAD = 1;
  var POPUP_LOOKAHEAD = 2;
  var LAST_CITY_KEY = 'amc_wbw_last_city_handle';
  var MAX_WARM_INFLIGHT = 2;
  var WARM_HOVER_MS = 80;

  var sessionsByBooking = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var fetchSeqByBooking = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var bags = Object.create(null);
  var inflight = Object.create(null);
  var bagEpoch = 0;
  var warmInflight = 0;

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function cal() {
    return global.WorkshopBookingCalendarWindow || null;
  }

  function ymKey(year, month1to12) {
    var C = cal();
    if (C && typeof C.ymKey === 'function') return C.ymKey(year, month1to12);
    return year + '-' + pad2(month1to12);
  }

  function shiftYm(year, month1to12, delta) {
    var C = cal();
    if (C && typeof C.shiftYm === 'function') return C.shiftYm(year, month1to12, delta);
    var d = new Date(year, month1to12 - 1 + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }

  function monthDateBounds(year, month1to12) {
    var C = cal();
    if (C && typeof C.monthDateBounds === 'function') return C.monthDateBounds(year, month1to12);
    var last = new Date(year, month1to12, 0).getDate();
    return {
      from: year + '-' + pad2(month1to12) + '-01',
      to: year + '-' + pad2(month1to12) + '-' + pad2(last),
    };
  }

  function windowDateBounds(year, month1to12, lookaheadMonths) {
    var C = cal();
    if (C && typeof C.windowDateBounds === 'function') return C.windowDateBounds(year, month1to12, lookaheadMonths);
    var start = monthDateBounds(year, month1to12);
    var endYm = shiftYm(year, month1to12, lookaheadMonths);
    var end = monthDateBounds(endYm.year, endYm.month);
    return { from: start.from, to: end.to };
  }

  function shopTodayKey() {
    try {
      var fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      return fmt.format(new Date());
    } catch (_) {
      var d = new Date();
      return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
    }
  }

  function initialWindow(lookaheadMonths) {
    var today = shopTodayKey();
    var y = Number(today.slice(0, 4));
    var m = Number(today.slice(5, 7));
    var b = windowDateBounds(y, m, lookaheadMonths);
    if (b.from < today) b.from = today;
    return b;
  }

  function ymKeysInRange(from, to) {
    var y = Number(String(from || '').slice(0, 4));
    var m = Number(String(from || '').slice(5, 7));
    var ey = Number(String(to || '').slice(0, 4));
    var em = Number(String(to || '').slice(5, 7));
    var out = [];
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(ey) || !Number.isFinite(em)) return out;
    var cur = { year: y, month: m };
    var guard = 0;
    while (guard < 36) {
      out.push(ymKey(cur.year, cur.month));
      if (cur.year === ey && cur.month === em) break;
      cur = shiftYm(cur.year, cur.month, 1);
      guard += 1;
    }
    return out;
  }

  function slotDateKey(slot) {
    var dk = slot && slot.date_key != null ? String(slot.date_key).trim() : '';
    if (/^\d{4}-\d{2}-\d{2}/.test(dk)) return dk.slice(0, 10);
    return '';
  }

  function slotIsUpcoming(slot) {
    var dk = slotDateKey(slot);
    return !!(dk && dk >= shopTodayKey());
  }

  function workshopTypeFromHandle(handle) {
    var h = String(handle || '').toLowerCase();
    if (h.indexOf('ceramic') !== -1) return 'ceramic';
    if (h.indexOf('resin') !== -1) return 'resin';
    if (h.indexOf('flower') !== -1 || h.indexOf('floral') !== -1) return 'flower';
    if (h.indexOf('mosaic') !== -1) return 'mosaic';
    return '';
  }

  function numericId(raw) {
    var s = String(raw || '').trim();
    if (!s) return '';
    if (s.indexOf('gid://') === 0) {
      var parts = s.split('/');
      return String(parts[parts.length - 1] || '').replace(/\D/g, '');
    }
    return s.replace(/\D/g, '');
  }

  function variantIdSet(list) {
    var out = Object.create(null);
    (list || []).forEach(function (id) {
      var k = numericId(id);
      if (k) out[k] = true;
    });
    return out;
  }

  function slotRaw(slot) {
    return (slot && slot._raw) || slot || {};
  }

  function slotAvailableVariants(slot) {
    var raw = slotRaw(slot);
    var av = raw.available_variants || raw.availableVariants;
    return Array.isArray(av) ? av : [];
  }

  /**
   * This city product is on the session — primary or joined (available_variants).
   * Unified rows keep the primary handle (often ceramic); that is not a type mismatch.
   */
  function slotBoundToCity(slot, opts) {
    if (!slot) return false;
    var productId = numericId(opts.productId);
    var cityHandle = String(opts.handle || opts.productHandle || '')
      .toLowerCase()
      .trim();
    var ids = opts._variantIdSet || variantIdSet(opts.variantIds);
    var raw = slotRaw(slot);
    var handle = String(slot.handle || slot.gid || raw.handle || '').toLowerCase();

    if (productId && handle.indexOf(productId) !== -1) return true;

    var slotPid = numericId(raw.product_id || raw.productId || slot.product_id || slot.productId);
    if (productId && slotPid && slotPid === productId) return true;

    if (cityHandle) {
      var slotPh = String(raw.product_handle || raw.productHandle || slot.product_handle || '')
        .toLowerCase()
        .trim();
      if (slotPh && slotPh === cityHandle) return true;
    }

    var vid = slot.variant_id != null ? slot.variant_id : raw.variant_id;
    if (vid != null && ids[numericId(vid)]) return true;

    var av = slotAvailableVariants(slot);
    for (var i = 0; i < av.length; i++) {
      var it = av[i];
      if (it == null) continue;
      if (typeof it !== 'object') {
        if (ids[numericId(it)]) return true;
        continue;
      }
      var aid = it.id != null ? it.id : it.variant_id != null ? it.variant_id : it.variantId;
      if (aid != null && ids[numericId(aid)]) return true;
      if (productId && numericId(it.product_id || it.productId) === productId) return true;
      if (cityHandle) {
        var ah = String(it.product_handle || it.productHandle || '')
          .toLowerCase()
          .trim();
        if (ah && ah === cityHandle) return true;
      }
    }
    return false;
  }

  function slotWorkshopType(slot) {
    var raw = slotRaw(slot);
    var fromHandle = workshopTypeFromHandle(slot && (slot.handle || slot.gid));
    if (fromHandle) return fromHandle;
    var explicit = String(
      raw.workshop_type || raw.workshopType || slot.workshop_type || slot.workshopType || ''
    )
      .toLowerCase()
      .trim();
    if (explicit) return workshopTypeFromHandle(explicit) || explicit;
    return workshopTypeFromHandle(raw.product_handle || raw.productHandle || '');
  }

  /**
   * Keep sessions that belong to this hub city product.
   * City-based stores: the events API is product-scoped. Unified rows often
   * keep a ceramic handle and empty available_variants — still this city's sessions.
   */
  function filterCitySlots(slots, opts) {
    opts = opts || {};
    var hubType = String(opts.hubType || '').toLowerCase().trim();
    var dateFrom = opts.dateFrom ? String(opts.dateFrom) : '';
    var dateTo = opts.dateTo ? String(opts.dateTo) : '';
    var upcoming = (slots || []).filter(slotIsUpcoming);
    if (!upcoming.length) return [];
    var boundOpts = {
      productId: opts.productId,
      handle: opts.handle,
      productHandle: opts.productHandle,
      variantIds: opts.variantIds,
      _variantIdSet: variantIdSet(opts.variantIds),
    };

    var inWindow = upcoming.filter(function (slot) {
      var dk = slotDateKey(slot);
      if (dateFrom && dk && dk < dateFrom) return false;
      if (dateTo && dk && dk > dateTo) return false;
      return true;
    });

    var bound = inWindow.filter(function (slot) {
      return slotBoundToCity(slot, boundOpts);
    });
    if (bound.length) return bound;

    if (hubType) {
      var typed = inWindow.filter(function (slot) {
        var st = slotWorkshopType(slot);
        return !!(st && st === hubType);
      });
      if (typed.length) return typed;
    }

    return inWindow;
  }

  function looksLikeUnboundedDump(slots, dateTo) {
    var n = 0;
    var beyond = 0;
    (slots || []).forEach(function (slot) {
      var dk = slotDateKey(slot);
      if (!dk) return;
      n += 1;
      if (dateTo && dk > dateTo) beyond += 1;
    });
    return n >= 40 && beyond >= 8;
  }

  function slotToEvent(slot, venueRef, venueName) {
    if (!slot || !slotDateKey(slot)) return null;
    var gid = slot.gid || slot.event_metaobject_gid || slot.id || '';
    return {
      id: gid,
      gid: gid,
      event_metaobject_gid: gid,
      handle: slot.handle || '',
      status: slot.status || 'active',
      display_status: slot.display_status || slot.status || 'active',
      capacity_remaining: slot.capacity_remaining,
      variant_id: slot.variant_id,
      schema_start: slot.schema_start || '',
      schema_end: slot.schema_end || '',
      date_key: slotDateKey(slot),
      time_label: slot.time_label || '',
      display_label: slot.display_label || '',
      venue_ref_id: venueRef,
      venue_name: venueName || slot.venue_name || '',
      _raw: slot._raw || slot,
    };
  }

  function eventDedupeKey(ev) {
    return String((ev && (ev.gid || ev.id || ev.event_metaobject_gid)) || '') +
      '|' +
      String((ev && ev.date_key) || '') +
      '|' +
      String((ev && ev.time_label) || '');
  }

  function slotDedupeKey(slot) {
    return String((slot && (slot.gid || slot.id)) || '') +
      '|' +
      slotDateKey(slot) +
      '|' +
      String((slot && slot.time_label) || '');
  }

  function cityBagKey(meta) {
    return String((meta && (meta.productId || meta.handle)) || '');
  }

  function getBag(meta) {
    var k = cityBagKey(meta);
    if (!k) k = '_';
    if (!bags[k]) {
      bags[k] = {
        byMonth: Object.create(null),
        loadedYm: Object.create(null),
        seen: Object.create(null),
      };
    }
    return bags[k];
  }

  function invalidateSessionBags() {
    bagEpoch += 1;
    bags = Object.create(null);
    Object.keys(inflight).forEach(function (k) {
      delete inflight[k];
    });
  }

  function markBagWindow(meta, from, to) {
    var bag = getBag(meta);
    ymKeysInRange(from, to).forEach(function (ym) {
      bag.loadedYm[ym] = true;
    });
  }

  function bagCovers(meta, from, to) {
    var bag = getBag(meta);
    var keys = ymKeysInRange(from, to);
    if (!keys.length) return false;
    return keys.every(function (ym) {
      return !!bag.loadedYm[ym];
    });
  }

  function ingestTypedSlots(meta, typed) {
    var bag = getBag(meta);
    (typed || []).forEach(function (slot) {
      var dk = slotDateKey(slot);
      if (!dk) return;
      var ym = dk.slice(0, 7);
      var key = slotDedupeKey(slot);
      if (bag.seen[key]) return;
      bag.seen[key] = true;
      if (!bag.byMonth[ym]) bag.byMonth[ym] = [];
      bag.byMonth[ym].push(slot);
      bag.loadedYm[ym] = true;
    });
  }

  function typedCount(meta) {
    var bag = getBag(meta);
    var n = 0;
    Object.keys(bag.byMonth).forEach(function (ym) {
      n += (bag.byMonth[ym] || []).length;
    });
    return n;
  }

  function slotsForWindow(meta, from, to) {
    var bag = getBag(meta);
    var out = [];
    ymKeysInRange(from, to).forEach(function (ym) {
      (bag.byMonth[ym] || []).forEach(function (slot) {
        var dk = slotDateKey(slot);
        if (from && dk < from) return;
        if (to && dk > to) return;
        out.push(slot);
      });
    });
    return out;
  }

  function numericEventId(val) {
    var H = global.WorkshopBookingWizardHelpers;
    if (H && typeof H.extractNumericEventIdForUrl === 'function') {
      return H.extractNumericEventIdForUrl(val);
    }
    var s = String(val || '').trim();
    if (!s) return '';
    var metaGid = /^gid:\/\/shopify\/Metaobject\/(\d+)$/i.exec(s);
    if (metaGid) return metaGid[1];
    if (/^\d+$/.test(s)) return s;
    return '';
  }

  function slotMatchesEventId(slot, eventId) {
    var want = numericEventId(eventId);
    if (!want || !slot) return false;
    var raw = slot._raw && typeof slot._raw === 'object' ? slot._raw : {};
    var cands = [
      slot.gid,
      slot.id,
      slot.event_metaobject_gid,
      raw.id,
      raw.gid,
      raw.event_metaobject_gid,
    ];
    for (var i = 0; i < cands.length; i++) {
      if (numericEventId(cands[i]) === want) return true;
    }
    return false;
  }

  function findBagSlotByEventId(meta, eventId) {
    if (!meta || !eventId) return null;
    var bag = getBag(meta);
    var months = Object.keys(bag.byMonth);
    for (var i = 0; i < months.length; i++) {
      var list = bag.byMonth[months[i]] || [];
      for (var j = 0; j < list.length; j++) {
        if (slotMatchesEventId(list[j], eventId)) return list[j];
      }
    }
    return null;
  }

  function findCitySlotByEventId(venue, eventId) {
    if (!venue || !eventId) return null;
    if (venue._wbHubMeta) {
      var fromBag = findBagSlotByEventId(venue._wbHubMeta, eventId);
      if (fromBag) return fromBag;
    }
    var events = Array.isArray(venue.events) ? venue.events : [];
    for (var i = 0; i < events.length; i++) {
      if (slotMatchesEventId(events[i], eventId)) return events[i];
    }
    return null;
  }

  function horizonDateTo(monthsAhead) {
    var n = monthsAhead == null ? 14 : Number(monthsAhead);
    if (!Number.isFinite(n) || n < 1) n = 14;
    var today = shopTodayKey();
    var y = Number(today.slice(0, 4));
    var m = Number(today.slice(5, 7));
    var end = shiftYm(y, m, n);
    return monthDateBounds(end.year, end.month).to;
  }

  function findRawSlotByEventId(meta, eventId) {
    if (!meta || !eventId) return null;
    var fromBag = findBagSlotByEventId(meta, eventId);
    if (fromBag) return fromBag;
    var raw = (getBag(meta).lastRaw || []);
    for (var i = 0; i < raw.length; i++) {
      if (slotMatchesEventId(raw[i], eventId)) return raw[i];
    }
    return null;
  }

  function pinEventSlot(meta, slot) {
    if (!meta || !slot) return;
    if (findBagSlotByEventId(meta, slot.gid || slot.id)) return;
    ingestTypedSlots(meta, [slot]);
  }

  /** Named session stays on the venue after a calendar window merge. */
  function ensureVenueNamedEvent(venue, eventId) {
    if (!venue || !eventId) return null;
    var events = Array.isArray(venue.events) ? venue.events : [];
    var i;
    for (i = 0; i < events.length; i++) {
      if (slotMatchesEventId(events[i], eventId)) return events[i];
    }
    var hit = findCitySlotByEventId(venue, eventId);
    if (!hit) return null;
    var ev = slotToEvent(hit, venue.venue_ref_id, venue.venue_name);
    if (!ev) return null;
    events = events.slice();
    events.push(ev);
    events.sort(function (a, b) {
      if (a.date_key !== b.date_key) return a.date_key < b.date_key ? -1 : 1;
      return String(a.time_label || '').localeCompare(String(b.time_label || ''));
    });
    venue.events = events;
    venue.has_events = true;
    venue._wbWaitlistVenue = false;
    var dk = slotDateKey(hit);
    if (dk) venue.next_date_key = dk;
    return ev;
  }

  function ensureEventInCity(booking, meta, eventId) {
    var found = findBagSlotByEventId(meta, eventId);
    if (found) return Promise.resolve(found);
    return fetchCityWindow(booking, meta, shopTodayKey(), horizonDateTo(14)).then(function () {
      found = findBagSlotByEventId(meta, eventId);
      if (found) return found;
      var rawHit = findRawSlotByEventId(meta, eventId);
      if (rawHit) {
        pinEventSlot(meta, rawHit);
        return rawHit;
      }
      return null;
    });
  }

  function normalizeCityToken(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function cityCardMatchesToken(btn, token) {
    var raw = String(token || '').trim();
    if (!raw || !btn) return false;
    var t = raw.toLowerCase();
    var slug = normalizeCityToken(raw);
    var venueId = String(btn.getAttribute('data-venue-id') || '').trim().toLowerCase();
    var handle = String(btn.getAttribute('data-handle') || '').trim().toLowerCase();
    var city = String(btn.getAttribute('data-city') || '').trim();
    var state = String(btn.getAttribute('data-state') || '').trim();
    if (venueId && (venueId === t || venueId === slug || normalizeCityToken(venueId) === slug)) return true;
    if (handle && handle === t) return true;
    if (city && normalizeCityToken(city) === slug) return true;
    if (city && state) {
      var withState = normalizeCityToken(city + '-' + state);
      var labeled = normalizeCityToken(city + ', ' + state);
      if (withState === slug || labeled === slug) return true;
    }
    var label = city && state ? city + ', ' + state : city;
    if (label && label.toLowerCase() === t) return true;
    return false;
  }

  function findCityCard(booking, token) {
    var wrap = cityWrap(booking);
    if (!wrap || !token) return null;
    var cards = wrap.querySelectorAll('[data-wbw-hub-city]');
    for (var i = 0; i < cards.length; i++) {
      if (cityCardMatchesToken(cards[i], token)) return cards[i];
    }
    return null;
  }

  function markLoadedMonths(venue, from, to, meta) {
    venue._wbLoadedMonths = Object.create(null);
    ymKeysInRange(from, to).forEach(function (ym) {
      venue._wbLoadedMonths[ym] = true;
    });
    var bag = meta ? getBag(meta) : null;
    if (bag) {
      Object.keys(bag.loadedYm).forEach(function (ym) {
        venue._wbLoadedMonths[ym] = true;
      });
    }
  }

  function mergeWindowEvents(venue, slots, from, to) {
    var ref = String((venue && venue.venue_ref_id) || '').trim();
    var name = (venue && venue.venue_name) || '';
    var existing = Array.isArray(venue.events) ? venue.events : [];
    var kept = [];
    var seen = Object.create(null);
    existing.forEach(function (ev) {
      var dk = ev && ev.date_key != null ? String(ev.date_key).trim() : '';
      if (!dk) return;
      if (from && dk >= from && to && dk <= to) return;
      var key = eventDedupeKey(ev);
      if (seen[key]) return;
      seen[key] = true;
      kept.push(ev);
    });
    (slots || []).forEach(function (slot) {
      var ev = slotToEvent(slot, ref, name);
      if (!ev) return;
      var key = eventDedupeKey(ev);
      if (seen[key]) return;
      seen[key] = true;
      kept.push(ev);
    });
    kept.sort(function (a, b) {
      if (a.date_key !== b.date_key) return a.date_key < b.date_key ? -1 : 1;
      return String(a.time_label || '').localeCompare(String(b.time_label || ''));
    });
    venue.events = kept;
    venue.has_events = kept.length > 0;
    venue.next_date_key = kept.length ? kept[0].date_key : '';
  }

  function buildCityVenue(meta, slots, bounds, waitlist) {
    var handle = String((meta && meta.handle) || 'city');
    var label = String((meta && meta.label) || (meta && meta.city) || 'City').trim() || 'City';
    var ref = String((meta && meta.venueId) || '').trim() || 'hub-city:' + handle;
    var kind = String((meta && meta.venueKind) || 'popup').toLowerCase();
    if (kind !== 'store') kind = 'popup';
    var events = [];
    var seen = Object.create(null);
    (slots || []).forEach(function (slot) {
      var ev = slotToEvent(slot, ref, label);
      if (!ev) return;
      var key = eventDedupeKey(ev);
      if (seen[key]) return;
      seen[key] = true;
      events.push(ev);
    });
    events.sort(function (a, b) {
      if (a.date_key !== b.date_key) return a.date_key < b.date_key ? -1 : 1;
      return String(a.time_label || '').localeCompare(String(b.time_label || ''));
    });
    var venue = {
      venue_ref_id: ref,
      venue_name: label,
      city_name: (meta && meta.city) || '',
      state: (meta && meta.state) || '',
      handle: handle,
      venue_handle: String((meta && meta.venueId) || handle || '').trim(),
      venue_kind: kind,
      lat: meta && meta.lat,
      lng: meta && meta.lng,
      venue_lat: meta && meta.lat,
      venue_lng: meta && meta.lng,
      has_events: events.length > 0,
      events: events,
      next_date_key: events.length ? events[0].date_key : '',
      _wbHubCitySessions: true,
      _wbHubMeta: meta,
      _wbWaitlistVenue: !!waitlist,
    };
    if (bounds) markLoadedMonths(venue, bounds.from, bounds.to, meta);
    return venue;
  }

  function readCityMeta(btn, wrap) {
    var city = String(btn.getAttribute('data-city') || '').trim();
    var state = String(btn.getAttribute('data-state') || '').trim();
    var handle = String(btn.getAttribute('data-handle') || '').trim();
    var kind = String(btn.getAttribute('data-venue-kind') || '').toLowerCase();
    var sfx = String(btn.getAttribute('data-template-suffix') || '').toLowerCase();
    if (kind !== 'store' && kind !== 'popup') {
      kind = sfx.indexOf('shop-') !== -1 || handle.toLowerCase().indexOf('orange-county') !== -1 ? 'store' : 'popup';
    }
    return {
      city: city,
      state: state,
      label: city && state ? city + ', ' + state : city || 'City',
      handle: handle,
      productId: String(btn.getAttribute('data-product-id') || '').trim(),
      venueId: String(btn.getAttribute('data-venue-id') || '').trim(),
      venueKind: kind,
      hubType: wrap ? String(wrap.getAttribute('data-wbw-hub-type') || '').trim() : '',
      variantIds: String(btn.getAttribute('data-variant-ids') || '')
        .split(',')
        .map(function (id) {
          return String(id || '').trim();
        })
        .filter(Boolean),
      lat: Number(btn.getAttribute('data-lat')),
      lng: Number(btn.getAttribute('data-lng')),
    };
  }

  function showPlaceholder(booking, msg) {
    var ph = booking && booking.querySelector ? booking.querySelector('[data-workshop-events-placeholder]') : null;
    if (!ph) return;
    ph.textContent = msg || '';
    if (msg) ph.classList.remove('pdp-wb-hidden');
    else ph.classList.add('pdp-wb-hidden');
  }

  function getS(booking) {
    return sessionsByBooking && booking ? sessionsByBooking.get(booking) : null;
  }

  function bumpSeq(booking) {
    if (!fetchSeqByBooking || !booking) return 1;
    var n = (fetchSeqByBooking.get(booking) || 0) + 1;
    fetchSeqByBooking.set(booking, n);
    return n;
  }

  function currentSeq(booking) {
    return fetchSeqByBooking && booking ? fetchSeqByBooking.get(booking) || 0 : 0;
  }

  function selectVenue(S, venue) {
    var state = S.state;
    var wizardRoot = S.wizardRoot;
    state.selectedVenue = venue;
    state.selectedVenueCard = null;
    state.venueDisplayLabel = venue.venue_name || '';
    state.flow = venue._wbWaitlistVenue ? 'waitlist' : 'booking';
    wizardRoot.setAttribute('data-wbw-flow', state.flow);
    if (S.bookingRoot) S.bookingRoot.__wbWizardVenueChosen = true;
    if (typeof S.syncVenueKindAttr === 'function') S.syncVenueKindAttr();
    if (typeof S.updateVenueContinue === 'function') S.updateVenueContinue();
    if (typeof S.updateProgressLabels === 'function') S.updateProgressLabels();
    if (typeof S.updateBranchTitles === 'function') S.updateBranchTitles();
    if (typeof S.updateProductVenueSeoCopy === 'function') S.updateProductVenueSeoCopy();
  }

  function startAfterCity(S, venue) {
    if (venue._wbWaitlistVenue) {
      if (typeof S.startWaitlistStep === 'function') S.startWaitlistStep();
      return;
    }
    if (venue.has_events && typeof S.goToNextFromVenue === 'function') return S.goToNextFromVenue();
    if (venue.venue_kind === 'store' && typeof S.startDatetimeStep === 'function') return S.startDatetimeStep();
    if (typeof S.startPopupBookingStep === 'function' && venue.has_events) return S.startPopupBookingStep();
    if (typeof S.startWaitlistStep === 'function') S.startWaitlistStep();
  }

  function applyCitySessions(S, meta, windowSlots, bounds, waitlist) {
    var venue = buildCityVenue(meta, windowSlots, bounds, waitlist);
    selectVenue(S, venue);
    return startAfterCity(S, venue);
  }

  /** Empty after membership filter. Popup → waitlist. Store → waitlist only on a foreign-type dump. */
  function venueShouldWaitlist(venue) {
    if (!venue || !venue._wbHubCitySessions) return false;
    if (Array.isArray(venue.events) && venue.events.length) return false;
    var meta = venue._wbHubMeta;
    if (!meta) return true;
    if (typedCount(meta) > 0) return false;
    var kind = String(meta.venueKind || venue.venue_kind || '').toLowerCase();
    /* Only the city's promised window answers "no sessions". A narrower window
       that happens to be empty is not enough. */
    var promised = metaBounds(meta);
    if (!bagCovers(meta, promised.from, promised.to)) return false;
    if (kind !== 'store') return true;
    var bag = getBag(meta);
    return looksLikeUnboundedDump(bag.lastRaw || [], bag.lastTo || '');
  }

  function waitForS(booking, tries) {
    var S = getS(booking);
    if (S) return Promise.resolve(S);
    if (!tries) return Promise.resolve(null);
    return new Promise(function (resolve) {
      setTimeout(function () {
        waitForS(booking, tries - 1).then(resolve);
      }, 40);
    });
  }

  function fetchCityWindow(booking, meta, from, to) {
    if (bagCovers(meta, from, to)) {
      return Promise.resolve(slotsForWindow(meta, from, to));
    }
    var API = global.WorkshopCityEvents;
    if (!API || typeof API.fetchSlots !== 'function') {
      return Promise.reject(new Error('Workshop events API is not configured.'));
    }
    var cacheKey = cityBagKey(meta) + '|' + from + '|' + to;
    if (inflight[cacheKey]) return inflight[cacheKey];
    var epoch = bagEpoch;
    var apiRoot =
      (booking && booking.closest && booking.closest('[data-workshop-booking-wizard]')) || booking;
    var p = API.fetchSlots(
      apiRoot,
      {
        productId: meta.productId,
        productHandle: meta.handle,
        workshopType: meta.hubType,
        dateFrom: from,
        dateTo: to,
      },
      null,
      { clipDateWindow: true }
    )
      .then(function (slots) {
        if (epoch !== bagEpoch) return slotsForWindow(meta, from, to);
        var bag = getBag(meta);
        bag.lastRaw = Array.isArray(slots) ? slots : [];
        bag.lastTo = to || '';
        var typed = filterCitySlots(slots, {
          hubType: meta.hubType,
          productId: meta.productId,
          handle: meta.handle,
          productHandle: meta.handle,
          variantIds: meta.variantIds,
          dateFrom: from,
          dateTo: to,
        });
        ingestTypedSlots(meta, typed);
        markBagWindow(meta, from, to);
        if (!typed.length && looksLikeUnboundedDump(slots, to)) {
          (slots || []).forEach(function (slot) {
            var dk = slotDateKey(slot);
            if (dk.length >= 7) bag.loadedYm[dk.slice(0, 7)] = true;
          });
        }
        return slotsForWindow(meta, from, to);
      })
      .then(
        function (windowSlots) {
          delete inflight[cacheKey];
          return windowSlots;
        },
        function (err) {
          delete inflight[cacheKey];
          throw err;
        }
      );
    inflight[cacheKey] = p;
    return p;
  }

  function prefetchNextMonth(booking, venue, year, month1to12, lookahead) {
    var next = shiftYm(year, month1to12, lookahead + 1);
    var ym = ymKey(next.year, next.month);
    if (venue._wbLoadedMonths && venue._wbLoadedMonths[ym]) return;
    var meta = venue._wbHubMeta;
    if (!meta) return;
    var bounds = monthDateBounds(next.year, next.month);
    fetchCityWindow(booking, meta, bounds.from, bounds.to)
      .then(function (slots) {
        mergeWindowEvents(venue, slots, bounds.from, bounds.to);
        markLoadedMonths(venue, bounds.from, bounds.to, meta);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        try {
          console.warn('[wbw-hub] month prefetch failed', err);
        } catch (_) {}
      });
  }

  function ensureCalendarMonths(booking, venue, year, month1to12, opts) {
    opts = opts || {};
    if (!venue || !venue._wbHubCitySessions) return Promise.resolve(venue);
    var meta = venue._wbHubMeta;
    if (!meta) return Promise.resolve(venue);
    var lookahead = opts.lookahead != null ? Number(opts.lookahead) : 1;
    if (!Number.isFinite(lookahead) || lookahead < 0) lookahead = 1;
    var y = Number(year);
    var m = Number(month1to12);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
      return Promise.reject(new Error('invalid month'));
    }
    var bounds = windowDateBounds(y, m, lookahead);
    var today = shopTodayKey();
    if (bounds.from < today && ymKey(y, m) === today.slice(0, 7)) bounds.from = today;
    /* First load must cover the same window the city card promises. The calendar
       asks for viewMonth + its own lookahead, which is narrower — a city whose
       next session is further out would come back empty and read as sold out.
       Once that window is cached, later month navigation keeps its narrow range. */
    var promised = metaBounds(meta);
    if (promised && !bagCovers(meta, promised.from, promised.to)) {
      if (promised.from && promised.from < bounds.from) bounds.from = promised.from;
      if (promised.to && promised.to > bounds.to) bounds.to = promised.to;
    }
    return fetchCityWindow(booking, meta, bounds.from, bounds.to).then(function (slots) {
      mergeWindowEvents(venue, slots, bounds.from, bounds.to);
      markLoadedMonths(venue, bounds.from, bounds.to, meta);
      if (venue._wbRestoreEventId) ensureVenueNamedEvent(venue, venue._wbRestoreEventId);
      if (opts.prefetch !== false) prefetchNextMonth(booking, venue, y, m, lookahead);
      return venue;
    });
  }

  function reloadOpenCityCatalog(booking) {
    invalidateSessionBags();
    var S = getS(booking);
    if (!S || !S.state || !S.state.selectedVenue) return Promise.resolve();
    var venue = S.state.selectedVenue;
    if (!venue._wbHubCitySessions || !venue._wbHubMeta) return Promise.resolve();
    venue._wbLoadedMonths = Object.create(null);
    var vm = S.state.viewMonth instanceof Date ? S.state.viewMonth : new Date();
    return ensureCalendarMonths(booking, venue, vm.getFullYear(), vm.getMonth() + 1, { prefetch: true }).then(
      function () {
        if (typeof S.eventsToSlots === 'function') {
          S.state.slotList = S.eventsToSlots(venue.events || []);
        }
        if (typeof S.buildDaysMap === 'function') {
          S.state.daysWithEvents = S.buildDaysMap(S.state.slotList || []);
        }
        if (typeof S.resetLiveCursors === 'function') S.resetLiveCursors();
        if (S.state.branch === 'datetime' && typeof S.renderCalendar === 'function') {
          S.renderCalendar({ closeTimeDd: false, allowAutoSelectSingle: false });
        } else if (S.state.branch === 'variants' && typeof S.applyPopupLiveRefresh === 'function') {
          S.applyPopupLiveRefresh({
            daysChanged: true,
            structureChanged: true,
            selectionChanged: false,
            bookabilityChanged: true,
            bookBitChanged: true,
          });
        }
        if (typeof S.refreshLiveNow === 'function') S.refreshLiveNow({ force: true });
        return venue;
      }
    );
  }

  function hubSummary() {
    var HS = global.WorkshopHubSummary;
    if (!HS || typeof HS.getCityFor !== 'function') return null;
    if (typeof HS.isReady === 'function' && !HS.isReady()) return null;
    return HS;
  }

  function summaryForMeta(meta) {
    var HS = hubSummary();
    return HS ? HS.getCityFor(meta) : null;
  }

  function metaBounds(meta) {
    var look = meta && meta.venueKind === 'store' ? STORE_LOOKAHEAD : POPUP_LOOKAHEAD;
    return initialWindow(look);
  }

  /* Intent-based warm: fires on hover/focus so the window is already cached by
     the time the card is clicked. Shares fetchCityWindow's inflight map, so a
     click mid-flight reuses the same request instead of firing a second one. */
  function warmCity(booking, btn) {
    if (!booking || !btn) return;
    var wrap = cityWrap(booking);
    var meta = readCityMeta(btn, wrap);
    if (!cityBagKey(meta)) return;
    var summary = summaryForMeta(meta);
    if (summary && summary.has_events === false) return;
    var bounds = metaBounds(meta);
    if (bagCovers(meta, bounds.from, bounds.to)) return;
    if (warmInflight >= MAX_WARM_INFLIGHT) return;
    warmInflight += 1;
    fetchCityWindow(booking, meta, bounds.from, bounds.to)
      .catch(function () {})
      .then(function () {
        warmInflight -= 1;
      });
  }

  function rememberCity(meta) {
    try {
      if (meta && meta.handle) global.localStorage.setItem(LAST_CITY_KEY, meta.handle);
    } catch (_) {}
  }

  function lastCityHandle() {
    try {
      return String(global.localStorage.getItem(LAST_CITY_KEY) || '').trim();
    } catch (_) {
      return '';
    }
  }

  /* One idle warm for a city we already know the visitor cares about — the last
     city they picked, or the one matching their resolved location. No guessing. */
  function warmKnownCity(booking) {
    var wrap = cityWrap(booking);
    if (!wrap || wrap.__wbwHubWarmed) return;
    var cards = wrap.querySelectorAll('[data-wbw-hub-city]');
    if (!cards.length) return;
    var handle = lastCityHandle();
    var loc = global.__WORKSHOP_LOCATION__ || null;
    var city = loc && loc.city ? String(loc.city).trim().toLowerCase() : '';
    var stateCode = loc && loc.state ? String(loc.state).trim().toLowerCase() : '';
    var target = null;
    for (var i = 0; i < cards.length; i++) {
      var el = cards[i];
      if (handle && String(el.getAttribute('data-handle') || '').trim() === handle) {
        target = el;
        break;
      }
      if (
        !target &&
        city &&
        String(el.getAttribute('data-city') || '').trim().toLowerCase() === city &&
        (!stateCode || String(el.getAttribute('data-state') || '').trim().toLowerCase() === stateCode)
      ) {
        target = el;
      }
    }
    if (!target) return;
    wrap.__wbwHubWarmed = true;
    warmCity(booking, target);
  }

  /* Status only — never reorder or replace cards after first paint. */
  function paintCityCardStatus(booking) {
    var HS = hubSummary();
    var wrap = cityWrap(booking);
    if (!HS || !wrap) return;
    wrap.querySelectorAll('[data-wbw-hub-city]').forEach(function (el) {
      var summary =
        HS.getCity(String(el.getAttribute('data-handle') || '').trim()) ||
        HS.getCity(String(el.getAttribute('data-product-id') || '').trim());
      if (!summary) return;
      /* Only claim sold out when the adapter actually reported capacity. */
      var has =
        summary.has_events !== false &&
        (summary.capacity_known === false || summary.bookable_count > 0);
      el.setAttribute('data-has-events', has ? '1' : '0');
      if (summary.next_date_key) el.setAttribute('data-next-date', summary.next_date_key);
      else el.removeAttribute('data-next-date');
    });
  }

  function loadCity(booking, btn, extra) {
    extra = extra || {};
    var wrap = booking && booking.querySelector ? booking.querySelector('[data-wbw-hub-cities]') : null;
    var meta = readCityMeta(btn, wrap);
    if (extra.lat != null) meta.lat = extra.lat;
    if (extra.lng != null) meta.lng = extra.lng;
    if (extra.label) meta.label = extra.label;
    rememberCity(meta);
    var seq = bumpSeq(booking);
    var look = meta.venueKind === 'store' ? STORE_LOOKAHEAD : POPUP_LOOKAHEAD;
    var bounds = initialWindow(look);
    showPlaceholder(booking, '');

    function prepareVenue(windowSlots) {
      var summary = summaryForMeta(meta);
      var cached = Array.isArray(windowSlots) ? windowSlots : [];
      var covered = bagCovers(meta, bounds.from, bounds.to);
      var waitlist = false;
      if (cached.length) waitlist = false;
      else if (covered) waitlist = !!(summary && summary.has_events === false);
      var venue = buildCityVenue(meta, cached, bounds, waitlist);
      if (!waitlist && !cached.length) venue.has_events = true;
      if (extra.eventId) {
        venue._wbRestoreEventId = extra.eventId;
        var hit = findBagSlotByEventId(meta, extra.eventId) || findRawSlotByEventId(meta, extra.eventId);
        if (hit) {
          pinEventSlot(meta, hit);
          waitlist = false;
          venue._wbWaitlistVenue = false;
          venue.has_events = true;
          var dk = slotDateKey(hit);
          if (dk) {
            venue.next_date_key = dk;
            var y = Number(dk.slice(0, 4));
            var m = Number(dk.slice(5, 7));
            if (Number.isFinite(y) && Number.isFinite(m)) {
              var mb = monthDateBounds(y, m);
              mergeWindowEvents(venue, getBag(meta).byMonth[dk.slice(0, 7)] || [hit], mb.from, mb.to);
              markLoadedMonths(venue, mb.from, mb.to, meta);
            } else {
              mergeWindowEvents(venue, [hit], '', '');
            }
          } else {
            mergeWindowEvents(venue, [hit], '', '');
          }
        }
        ensureVenueNamedEvent(venue, extra.eventId);
      }
      return venue;
    }

    function openNextStep(S, windowSlots) {
      if (!S) {
        showPlaceholder(booking, "Couldn't load sessions. Try again.");
        enterCityPhase(booking);
        return Promise.resolve();
      }
      if (seq !== currentSeq(booking)) return Promise.resolve();
      var venue = prepareVenue(windowSlots);
      selectVenue(S, venue);
      return Promise.resolve(startAfterCity(S, venue)).then(function () {
        if (seq !== currentSeq(booking)) return;
        var branch = S.state && S.state.branch;
        if (branch && branch !== 'venue') enterVenuePhase(booking);
      });
    }

    function cachedWindow() {
      return bagCovers(meta, bounds.from, bounds.to) ? slotsForWindow(meta, bounds.from, bounds.to) : [];
    }

    function expandBoundsToDate(win, dateKey) {
      var dk = String(dateKey || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dk) || !win) return win;
      var y = Number(dk.slice(0, 4));
      var m = Number(dk.slice(5, 7));
      if (!Number.isFinite(y) || !Number.isFinite(m)) return win;
      var mb = monthDateBounds(y, m);
      var from = win.from;
      var to = win.to;
      if (mb.from && (!from || mb.from < from)) from = mb.from;
      if (mb.to && (!to || mb.to > to)) to = mb.to;
      return { from: from, to: to };
    }

    function slotsReady() {
      var win = expandBoundsToDate(bounds, extra.date);
      var needEvent = String(extra.eventId || '').trim();
      return fetchCityWindow(booking, meta, win.from, win.to).then(function (slots) {
        if (!needEvent) return slots;
        var hit = findBagSlotByEventId(meta, needEvent) || findRawSlotByEventId(meta, needEvent);
        if (hit) {
          pinEventSlot(meta, hit);
          return slotsForWindow(meta, win.from, win.to);
        }
        return fetchCityWindow(booking, meta, shopTodayKey(), horizonDateTo(14)).then(function () {
          hit = findBagSlotByEventId(meta, needEvent) || findRawSlotByEventId(meta, needEvent);
          if (hit) pinEventSlot(meta, hit);
          return slotsForWindow(meta, win.from, win.to);
        });
      });
    }

    if (extra.waitForWindow || extra.eventId) {
      return waitForS(booking, 25)
        .then(function (S) {
          return slotsReady().then(function (slots) {
            return openNextStep(S || getS(booking), slots);
          });
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          try {
            console.warn('[wbw-hub] city load failed', err);
          } catch (_) {}
          showPlaceholder(booking, "Couldn't load sessions. Try again.");
          enterCityPhase(booking);
        });
    }

    var ready = getS(booking);
    if (ready) {
      openNextStep(ready, cachedWindow());
      return Promise.resolve();
    }
    return waitForS(booking, 25).then(function (readyS) {
      if (seq !== currentSeq(booking)) return;
      return openNextStep(readyS || getS(booking), cachedWindow());
    });
  }

  function paintCitySelection(wrap, btn) {
    if (!wrap || !btn) return;
    wrap.querySelectorAll('[data-wbw-hub-city]').forEach(function (el) {
      var on = el === btn;
      el.classList.toggle('is-selected', on);
      el.classList.toggle('wbw-venue-selected', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function activateCityCard(booking, btn, extra) {
    extra = extra || {};
    var wrap = cityWrap(booking);
    var lat = Number(btn.getAttribute('data-lat'));
    var lng = Number(btn.getAttribute('data-lng'));
    var city = String(btn.getAttribute('data-city') || '').trim();
    var state = String(btn.getAttribute('data-state') || '').trim();
    var label = extra.label || (city && state ? city + ', ' + state : city || 'City');
    paintCitySelection(wrap, btn);
    var hubS = getS(booking);
    if (hubS && typeof hubS.clearMissingControls === 'function') hubS.clearMissingControls();
    if (typeof window !== 'undefined') {
      window.__WB_USER_CHOSEN_LOCATION__ = true;
      window.__WB_HUB_CITY_CHOSEN__ = true;
      var loc = {
        label: label,
        city: city,
        state: state,
        country: 'US',
        source: extra.source || 'user',
      };
      if (Number.isFinite(lat)) loc.lat = lat;
      if (Number.isFinite(lng)) loc.lng = lng;
      window.__WORKSHOP_LOCATION__ = loc;
    }
    var locInput = document.getElementById('workshop-location-input');
    if (locInput) locInput.value = label;
    var cityToken =
      String(btn.getAttribute('data-venue-id') || '').trim() ||
      String(btn.getAttribute('data-handle') || '').trim();
    var H = global.WorkshopBookingWizardHelpers;
    if (H && typeof H.updateWizardBookingUrl === 'function') {
      if (extra.source === 'deeplink') {
        H.updateWizardBookingUrl({ city: cityToken });
      } else {
        H.updateWizardBookingUrl({
          city: cityToken,
          eventId: '',
          date: '',
          step: '',
          variantId: '',
        });
      }
    }
    return loadCity(booking, btn, {
      city: city,
      state: state,
      label: label,
      lat: Number.isFinite(lat) ? lat : extra.lat,
      lng: Number.isFinite(lng) ? lng : extra.lng,
      waitForWindow: extra.waitForWindow === true,
      eventId: extra.eventId,
      date: extra.date,
      source: extra.source,
    });
  }

  function applyUrlDeepLink(booking, opts) {
    opts = opts || {};
    var S = getS(booking);
    if (!S) {
      return waitForS(booking, 25).then(function (ready) {
        if (!ready) return false;
        return applyUrlDeepLink(booking, opts);
      });
    }
    var wizardRoot = S.wizardRoot;
    if (!booking || !wizardRoot) return Promise.resolve(false);
    if (wizardRoot.__wbwUrlDeepLinkComplete || wizardRoot.__wbwUserReachedDatetime) {
      return Promise.resolve(false);
    }
    var cityToken = String(opts.city || opts.venue || opts.venueToken || '').trim();
    var eventId = String(opts.eventId || '').trim();
    if (!cityToken && !eventId) return Promise.resolve(false);

    var btn = cityToken ? findCityCard(booking, cityToken) : null;
    if (!btn && eventId) {
      var H = global.WorkshopBookingWizardHelpers;
      var sectionId = wizardRoot.getAttribute('data-section-id') || '';
      var rows =
        H && typeof H.parseVenueWorkshopIndex === 'function' ? H.parseVenueWorkshopIndex(sectionId) : [];
      var row =
        H && typeof H.findIndexRowByEventId === 'function' ? H.findIndexRowByEventId(rows, eventId) : null;
      if (row) {
        btn =
          findCityCard(booking, String(row.venue_handle || '').trim()) ||
          findCityCard(booking, String(row.product_handle || '').trim()) ||
          findCityCard(booking, String(row.city_name || row.city || '').trim());
      }
    }
    if (!btn && !cityToken && lastCityHandle()) btn = findCityCard(booking, lastCityHandle());
    if (!btn) {
      if (typeof S.clearUrlRestore === 'function') S.clearUrlRestore();
      try {
        document.documentElement.removeAttribute('data-wbw-deeplink');
      } catch (_) {}
      return Promise.resolve(false);
    }

    wizardRoot.__wbwUrlDeepLinkComplete = true;
    var wantVariants = String(opts.step || '').toLowerCase() === 'variants';
    return activateCityCard(booking, btn, {
      /* City-only + variants: open the step immediately (same as a tap).
         Waiting on the window left Step 1 empty while cities were already hidden. */
      waitForWindow: !!(eventId || (!wantVariants && !opts.date)),
      eventId: eventId,
      date: String(opts.date || '').slice(0, 10),
      source: 'deeplink',
    })
      .then(function () {
        if (eventId) ensureVenueNamedEvent(S.state && S.state.selectedVenue, eventId);
        if (typeof S.applyDeepLinkSlotFromUrl === 'function') S.applyDeepLinkSlotFromUrl();
        return true;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return false;
        try {
          console.warn('[wbw-hub] deep-link failed', err);
        } catch (_) {}
        try {
          document.documentElement.removeAttribute('data-wbw-deeplink');
        } catch (_) {}
        if (typeof S.clearUrlRestore === 'function') S.clearUrlRestore();
        enterCityPhase(booking);
        return false;
      });
  }

  function cityWrap(booking) {
    return booking && booking.querySelector ? booking.querySelector('[data-wbw-hub-cities]') : null;
  }

  function cityPickerVisible(booking) {
    var wrap = cityWrap(booking);
    return !!(wrap && !wrap.hidden && wrap.querySelector('[data-wbw-hub-city]'));
  }

  function hubHaversineKm(aLat, aLng, bLat, bLng) {
    var r = 6371;
    var dLat = ((bLat - aLat) * Math.PI) / 180;
    var dLng = ((bLng - aLng) * Math.PI) / 180;
    var s =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * r * Math.asin(Math.min(1, Math.sqrt(s)));
  }

  function snapshotCityOrder(wrap) {
    if (!wrap || wrap.__wbwHubCityOrder) return;
    wrap.__wbwHubCityOrder = Array.prototype.slice.call(wrap.querySelectorAll('[data-wbw-hub-city]'));
  }

  function cityCardDistKm(el, origin) {
    if (!el || !origin) return 99999;
    var lat = Number(el.getAttribute('data-lat'));
    var lng = Number(el.getAttribute('data-lng'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 99999;
    return hubHaversineKm(Number(origin.lat), Number(origin.lng), lat, lng);
  }

  function restoreCityOrder(wrap) {
    if (!wrap) return;
    var order = wrap.__wbwHubCityOrder;
    if (!Array.isArray(order) || !order.length) {
      wrap.querySelectorAll('[data-wbw-hub-city]').forEach(function (el) {
        el.hidden = false;
      });
      return;
    }
    order.forEach(function (el) {
      if (!el) return;
      wrap.appendChild(el);
      el.hidden = false;
    });
  }

  /* Search origin (Google place / Near me) may reorder. Status paint must not. */
  function applySearchOrigin(booking, origin) {
    var wrap = cityWrap(booking);
    if (!wrap || wrap.hidden) return;
    snapshotCityOrder(wrap);
    var lat = origin ? Number(origin.lat) : NaN;
    var lng = origin ? Number(origin.lng) : NaN;
    if (!origin || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      wrap.__wbwHubSearchOrigin = null;
      restoreCityOrder(wrap);
      return;
    }
    wrap.__wbwHubSearchOrigin = { lat: lat, lng: lng, label: origin.label || '', source: origin.source || '' };
    var cards = wrap.__wbwHubCityOrder.slice();
    cards.sort(function (a, b) {
      return cityCardDistKm(a, wrap.__wbwHubSearchOrigin) - cityCardDistKm(b, wrap.__wbwHubSearchOrigin);
    });
    cards.forEach(function (el) {
      if (!el) return;
      wrap.appendChild(el);
      el.hidden = false;
    });
  }

  function clearSearchOrigin(booking) {
    applySearchOrigin(booking, null);
  }

  function matchCities(booking, query) {
    var wrap = cityWrap(booking);
    if (!wrap) return [];
    var q = String(query || '')
      .trim()
      .toLowerCase();
    if (q.length < 2) return [];
    var out = [];
    wrap.querySelectorAll('[data-wbw-hub-city]').forEach(function (el) {
      var city = String(el.getAttribute('data-city') || '').trim();
      var state = String(el.getAttribute('data-state') || '').trim();
      var cityLow = city.toLowerCase();
      var stateLow = state.toLowerCase();
      var label = city + (state ? ', ' + state : '');
      var labelLow = label.toLowerCase();
      var score = 0;
      if (cityLow === q || labelLow === q) score = 400;
      else if (cityLow.indexOf(q) === 0) score = Math.max(220, 320 - Math.min(90, (cityLow.length - q.length) * 6));
      else if ((' ' + cityLow.replace(/-/g, ' ')).indexOf(' ' + q) !== -1) score = 200;
      else if (stateLow === q || stateLow.indexOf(q) === 0) score = 80;
      else if (q.length >= 4 && labelLow.indexOf(q) !== -1) score = 120;
      if (score <= 0) return;
      var img = el.querySelector('img');
      out.push({
        kind: 'city',
        btn: el,
        city: city,
        state: state,
        label: label,
        score: score,
        image: img ? img.getAttribute('src') || '' : '',
        venue: { venue_name: city, city_name: city, city: city }
      });
    });
    out.sort(function (a, b) {
      return (b.score || 0) - (a.score || 0);
    });
    return out.slice(0, 6);
  }

  function changeCityBtn(booking) {
    return booking && booking.querySelector ? booking.querySelector('[data-wbw-hub-change-city]') : null;
  }

  function setChangeCityVisible(booking, on) {
    var btn = changeCityBtn(booking);
    if (!btn) return;
    if (on) btn.removeAttribute('hidden');
    else btn.setAttribute('hidden', '');
  }

  function enterVenuePhase(booking) {
    var wrap = cityWrap(booking);
    if (!wrap) return;
    wrap.hidden = true;
    wrap.setAttribute('aria-hidden', 'true');
    setChangeCityVisible(booking, true);
  }

  function citySearchInput() {
    return document.getElementById('workshop-location-input');
  }

  function applyCityFilter(wrap, query) {
    if (!wrap) return;
    var q = String(query || '')
      .trim()
      .toLowerCase();
    wrap.querySelectorAll('[data-wbw-hub-city]').forEach(function (el) {
      if (q.length < 2) {
        el.hidden = false;
        return;
      }
      var hay = String(el.getAttribute('data-search') || '').toLowerCase();
      el.hidden = hay.indexOf(q) === -1;
    });
  }

  /* Coming back to the city list must not land on the previous search: the
     query text, origin sort, and hidden cards all survive the round trip
     otherwise, so the step looks frozen on a one-city (or empty) list. */
  function resetCityFilter(wrap) {
    var input = citySearchInput();
    if (input && input.value) input.value = '';
    var booking = wrap && wrap.closest ? wrap.closest('.pdp-workshop-booking') : null;
    if (booking) {
      clearSearchOrigin(booking);
      if (typeof booking.__wbWizardSyncSearchClearBtn === 'function') booking.__wbWizardSyncSearchClearBtn();
      if (typeof booking.__wbWizardSyncSearchReel === 'function') booking.__wbWizardSyncSearchReel();
    } else {
      applyCityFilter(wrap, '');
    }
  }

  function enterCityPhase(booking) {
    var wrap = cityWrap(booking);
    if (!wrap) return;
    wrap.hidden = false;
    wrap.removeAttribute('aria-hidden');
    resetCityFilter(wrap);
    setChangeCityVisible(booking, false);
    bumpSeq(booking);
    if (typeof window !== 'undefined') window.__WB_HUB_CITY_CHOSEN__ = false;
    if (booking) booking.__wbWizardVenueChosen = false;
    var eventsRoot = booking.querySelector('[data-workshop-events]');
    if (eventsRoot) eventsRoot.innerHTML = '';
    showPlaceholder(booking, '');
    try {
      document.dispatchEvent(new CustomEvent('wbw:hub-change-city', { detail: { bookingRoot: booking } }));
    } catch (_) {}
  }

  function bindCityWarming(booking, wrap) {
    var timer = 0;
    var lastBtn = null;
    function schedule(btn) {
      if (!btn || btn === lastBtn) return;
      lastBtn = btn;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = 0;
        warmCity(booking, btn);
      }, WARM_HOVER_MS);
    }
    function fromEvent(e) {
      var t = e && e.target;
      return t && t.closest ? t.closest('[data-wbw-hub-city]') : null;
    }
    wrap.addEventListener('pointerover', function (e) {
      schedule(fromEvent(e));
    });
    wrap.addEventListener('focusin', function (e) {
      schedule(fromEvent(e));
    });
    wrap.addEventListener(
      'touchstart',
      function (e) {
        var btn = fromEvent(e);
        if (btn) warmCity(booking, btn);
      },
      { passive: true }
    );
    var idle = global.requestIdleCallback || function (cb) { return setTimeout(cb, 400); };
    idle(function () {
      warmKnownCity(booking);
    });
  }

  function bindCityPicker(booking) {
    var wrap = cityWrap(booking);
    if (!wrap || wrap.__wbwHubBound) return;
    wrap.__wbwHubBound = true;
    bindCityWarming(booking, wrap);
    var HS = global.WorkshopHubSummary;
    if (HS && typeof HS.init === 'function') {
      HS.init(wrap);
      if (typeof HS.onUpdate === 'function') {
        HS.onUpdate(function () {
          paintCityCardStatus(booking);
          warmKnownCity(booking);
        });
      }
      if (typeof HS.isReady === 'function' && HS.isReady()) paintCityCardStatus(booking);
    }
    wrap.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-wbw-hub-city]') : null;
      if (!btn || !wrap.contains(btn)) return;
      e.preventDefault();
      var lat = Number(btn.getAttribute('data-lat'));
      var lng = Number(btn.getAttribute('data-lng'));
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      activateCityCard(booking, btn, { source: 'user' });
    });
    var changeBtn = changeCityBtn(booking);
    if (changeBtn && !changeBtn.__wbwHubBound) {
      changeBtn.__wbwHubBound = true;
      changeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        enterCityPhase(booking);
      });
    }
    if (!wrap.__wbwHubBackBound) {
      wrap.__wbwHubBackBound = true;
      document.addEventListener('wbw:hub-back-to-city', function (e) {
        var d = (e && e.detail) || {};
        if (d.bookingRoot && booking && d.bookingRoot !== booking) return;
        enterCityPhase(booking);
      });
    }
    /* Name + Google nearby live on the shared wizard search dropdown.
       Do not hide city cards while typing — that emptied the grid for
       Google-only queries (Find a Workshop keeps the catalog visible). */
  }

  function attach(S) {
    if (!S || !S.wizardRoot || !S.bookingRoot) return;
    if (S.wizardRoot.getAttribute('data-wbw-hub') !== '1') return;
    if (sessionsByBooking) sessionsByBooking.set(S.bookingRoot, S);
    bindCityPicker(S.bookingRoot);
    if (S.wizardRoot.__wbwHubAttached) return;
    S.wizardRoot.__wbwHubAttached = true;
    document.addEventListener('wbw:hub-change-city', function (e) {
      var d = (e && e.detail) || {};
      if (d.bookingRoot && S.bookingRoot && d.bookingRoot !== S.bookingRoot) return;
      bumpSeq(S.bookingRoot);
      if (typeof S.clearUrlRestore === 'function') S.clearUrlRestore();
      if (typeof S.clearVenueSelection === 'function') S.clearVenueSelection();
      if (typeof S.setBranch === 'function') S.setBranch('venue', { scroll: true });
    });
  }

  function bootPickers() {
    document.querySelectorAll('[data-wbw-hub-cities]').forEach(function (wrap) {
      var booking = wrap.closest ? wrap.closest('.pdp-workshop-booking') : null;
      if (booking) bindCityPicker(booking);
    });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootPickers);
    } else {
      bootPickers();
    }
  }

  global.WorkshopBookingWizardHub = {
    attach: attach,
    loadCity: loadCity,
    applyUrlDeepLink: applyUrlDeepLink,
    findCityCard: findCityCard,
    findCitySlotByEventId: findCitySlotByEventId,
    ensureVenueNamedEvent: ensureVenueNamedEvent,
    filterCitySlots: filterCitySlots,
    ensureCalendarMonths: ensureCalendarMonths,
    venueShouldWaitlist: venueShouldWaitlist,
    reloadOpenCityCatalog: reloadOpenCityCatalog,
    invalidateSessionBags: invalidateSessionBags,
    cityPickerVisible: cityPickerVisible,
    applySearchOrigin: applySearchOrigin,
    clearSearchOrigin: clearSearchOrigin,
    matchCities: matchCities
  };
})(typeof window !== 'undefined' ? window : this);
